import {
  assignAttributeStateToLegends,
  assignAttributeStateToTLLegends,
  convertTimeToMinutes,
  generateLegend,
  getMergedYAxisList,
  getPropertyAxisAndIsAdded,
  getTimeIndex,
  intervalToMinutes,
  legendIDGenerator,
  legendName
} from "../common"
import moment from "moment"
import { isArray, isString, toNumber, uniq } from "lodash"
import {
  chartDateFormat,
  CIRCUIT_COUNT,
  constantValues,
  reduxStoreTimeStampFormat
} from "../../constant"
import {
  TIMELINE_PROPERTY_TYPE,
  transformTimeLinechartNew
} from "./timeLineChart"

import { extractChartCustomHpathProperties } from "../Payload/Performance/generateChartPayload"
import { generateLegendProps_Type } from "../../ChartContainer/Types/helper"
import { generateExceptionLegends } from "../Exceptions/helper"
import { generateSuppressionLegends } from "../Suppressions/helper"
import { generateSuppressionBars } from "../Suppressions"
import { generateExceptionBars } from "../Exceptions/index"

export const STRAIGHT_CURVE = "straightCurve"
export const transformScatterWithPerformanceCurvechart = ({
  tz,
  startDate,
  endDate,
  intervalMinutes = "15m",
  inputData,
  chartInfo,
  equipmentList,
  buildingId,
  equipmentId,
  selectedTypeEquipment = {},
  addedProperties,
  oldLegends,
  oldTimeLineLegends = null,
  assignedAttributes = [],
  legendAttributesCombination
}: any) => {
  let calculatedMin = intervalToMinutes(intervalMinutes)
  let minimumCalculatedMin = intervalToMinutes(intervalMinutes)
  let minInterval = calculatedMin
  const defaultCalculatedMin = intervalToMinutes(intervalMinutes)
  let lastIndexPosition = (60 / calculatedMin) * 24 - 1
  const equipmentType = chartInfo?.equipmentType
  let legends: any = {}
  let timeLineLegends = {}
  const addedAxisList = addedProperties?.axisList || []
  const data = inputData?.data || {}
  const curveData = inputData?.curveData || []
  const newData = {}
  const newDataTimeline = {}
  const newCurveData = {}
  const allTimestampsObject = {}

  const endTimestamp = moment(endDate, chartDateFormat).add(1, "days")
  const localTimeStamp = Boolean(tz)
    ? moment(
        moment.tz(moment(), tz).format("MM-DD-YYYY HH:mm"),
        "MM-DD-YYYY HH:mm"
      )
    : moment()
  for (const id in data) {
    const paramData = data[id]
    const equipmentName = equipmentList?.find((obj) => obj?.id === id)?.name
    const selectedEquipment = equipmentId === id
    const building = buildingId === id

    const properties = paramData?.properties
    const sortedDates = Object.keys(paramData?.values || {})
      .filter((date) => date != "frq")
      .sort(
        (a: string, b: string) =>
          new Date(a).getTime() - new Date(b).getTime()
      )

    for (const propertyKey in properties) {
      calculatedMin = defaultCalculatedMin
      minInterval = defaultCalculatedMin
      lastIndexPosition = (60 / minInterval) * 24 - 1
      const property = properties[propertyKey]
      const propertyIndex = property?.index
      const propertyName = property?.name || ""

      const { axis, isUserAddedProperty, legendId } =
        getPropertyAxisAndIsAdded({
          chartInfo,
          propertyKey,
          equipmentType,
          equipmentList,
          id,
          addedAxisList
        })
      const name = legendName({
        building,
        selectedEquipment,
        propertyName,
        equipmentName
      })
      const xType = axis?.axisType?.toLowerCase() === "x"
      let legendObj: any
      if (axis?.axisName === "timeline") {
        legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          propertyKey,
          type: "timeline",
          isUserAddedProperty,
          oldLegends: oldTimeLineLegends,
          propertyType: TIMELINE_PROPERTY_TYPE
        })
        timeLineLegends[legendId] = legendObj
      } else {
        const obj: generateLegendProps_Type = {
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          isUserAddedProperty,
          oldLegends
        }
        if (xType) {
          obj.additionalAttributes = {
            xAxis: true,
            disabled: true
          }
        }
        obj.additionalAttributes = {
          ...(obj.additionalAttributes || {}),
          specificProperties: {
            equipID: id,
            propertyKey: propertyKey
          }
        }
        legendObj = generateLegend(obj)
        legends[legendObj?.id] = legendObj
      }

      let currentTimestamp = moment(startDate, chartDateFormat).locale("en")
      let prevValue = null
      while (
        currentTimestamp.isSameOrBefore(endTimestamp) &&
        currentTimestamp.isSameOrBefore(localTimeStamp)
      ) {
        const dateKey = currentTimestamp.format("YYYY-MM-DD")
        const extractTime = currentTimestamp.format("HH:mm")
        const timetoMin = convertTimeToMinutes(extractTime)

        const dateAvailable = paramData?.values?.[dateKey]
        if (dateAvailable) {
          const dateIndexPosition = sortedDates.indexOf(dateKey)
          const extractedInterval =
            paramData.values?.frq?.[dateIndexPosition]?.split("|")?.[
              propertyIndex
            ]
          calculatedMin = extractedInterval / 60 || defaultCalculatedMin
          minInterval =
            paramData?.values?.[dateKey]?.["intervalInSeconds"] / 60 ||
            defaultCalculatedMin
          lastIndexPosition = (60 / minInterval) * 24 - 1

          if (calculatedMin < minimumCalculatedMin) {
            minimumCalculatedMin = calculatedMin
          }
        }
        const timeIndexPosition = getTimeIndex(timetoMin, minInterval)

        let finalValue
        if (dateAvailable) {
          const valueArray = paramData?.values?.[dateKey]?.[timeIndexPosition]
          const valueArrayIsArray = isArray(valueArray)
          const arrayValues = valueArrayIsArray ? valueArray : []
          let value = arrayValues[propertyIndex]

          const isOutOfRange = value?.toString()?.startsWith("_")
          if (isOutOfRange && value?.toString()?.length > 1) {
            value = value?.toString()?.substring(1)
          }

          const isNull = constantValues?.includes(value)
          const isTimeline = axis?.axisName === "timeline"

          const checkedValue =
            isNull || (isTimeline ? false : !isFinite(toNumber(value)))
              ? null
              : isTimeline
              ? value
              : parseFloat(value)

          if ((isString(value) && value?.length === 0) || !valueArrayIsArray) {
            finalValue = prevValue
          } else {
            finalValue = checkedValue
          }

          if (timeIndexPosition === lastIndexPosition) {
            prevValue = null
          } else {
            prevValue = finalValue
          }
        } else {
          finalValue = null
          prevValue = null
        }

        const dateVal = currentTimestamp.format(reduxStoreTimeStampFormat)

        if (axis?.axisName === "timeline") {
          if (!newDataTimeline?.[legendId]) {
            newDataTimeline[legendId] = {
              data: [],
              timestamps: []
            }
          }
        } else {
          if (!newData?.[legendId]) {
            newData[legendId] = {
              data: [],
              timestamps: []
            }
          }
        }
        if (!allTimestampsObject[dateVal]) {
          allTimestampsObject[dateVal] = 1
        }
        if (axis?.axisName === "timeline") {
          newDataTimeline[legendId].data.push(finalValue)
          newDataTimeline[legendId].timestamps.push(dateVal)
        } else {
          newData[legendId].data.push(finalValue)
          newData[legendId].timestamps.push(dateVal)
        }
        currentTimestamp = currentTimestamp.add(calculatedMin, "minutes")
      }
    }
  }
  const allTimestamps = Object.keys(allTimestampsObject).sort()
  const instancesCount = uniq(
    curveData?.map((obj) => (obj?.instance || "")?.toLowerCase())
  )?.length
  const selectedEquipmentCustomHpathProperties =
    extractChartCustomHpathProperties(
      selectedTypeEquipment?.compCondType,
      selectedTypeEquipment?.instance
    )
  const circuitCount = selectedEquipmentCustomHpathProperties?.[CIRCUIT_COUNT]
  curveData?.forEach((curve) => {
    const equipmentName = equipmentList?.find(
      (obj) => obj?.id === curve?.equipmentId
    )?.name
    const selectedEquipment = curve?.equipmentId === equipmentId
    const instanceName =
      instancesCount > 1 ? "Ckt " + curve?.instance?.slice(-1) : "Ckt"
    const legendId = legendIDGenerator(
      equipmentId,
      curve?.curveType,
      curve?.instance?.toLowerCase()
    )
    const name = `${
      selectedEquipment ? "" : equipmentName + " "
    }#$${instanceName} ${curve?.curveType}`
    const legendArray: any = Object.values(legends)
    const xNamePropName: any =
      legendArray?.find((obj: any) => {
        if (circuitCount === 1) {
          return (
            obj?.axisName?.toLowerCase() === "x" &&
            !(obj?.name?.toLowerCase() || "")?.includes("Ckt"?.toLowerCase())
          )
        } else if (circuitCount > 1) {
          return (
            obj?.axisName?.toLowerCase() === "x" &&
            (obj?.name?.toLowerCase() || "")?.includes(
              (instanceName || "")?.toLowerCase()
            )
          )
        } else {
          return false
        }
      })?.name || ""
    const xName = `${
      selectedEquipment ? "" : equipmentName + " "
    }${xNamePropName}`
    const obj: generateLegendProps_Type = {
      legendId,
      name,
      axisName: "y",
      oldLegends,
      type: STRAIGHT_CURVE
    }
    obj.additionalAttributes = {
      xName
    }
    const legendObj: any = generateLegend(obj)
    legends[legendObj?.id] = legendObj
    newCurveData[legendId] = curve?.characteristicCurveDatas
  })

  const noDataArray = Object.values(legends)?.map((obj: any) => {
    if (obj?.type === STRAIGHT_CURVE) {
      const curveArray = newCurveData?.[obj?.id] || []
      const data = []
      curveArray?.forEach((obj) => {
        data.push(toNumber(obj?.curveXAxisValue))
        data.push(toNumber(obj?.curveYAxisValue))
      })
      const noData = data?.every((e) => !Boolean(e) && e !== 0)
      return noData
    } else {
      const noData = (newData?.[obj?.id]?.data || [])?.every(
        (e) => !Boolean(e) && e !== 0
      )
      return noData
    }
  })

  const isNoData = noDataArray?.every((e) => Boolean(e))

  assignAttributeStateToLegends(
    legends,
    oldLegends,
    assignedAttributes,
    legendAttributesCombination
  )

  // Create/update Exception Legends
  const exceptionData = inputData?.exceptions?.data || {}
  const exceptionLegends = generateExceptionLegends({
    exceptionData,
    chartInfo,
    equipmentList,
    selectedEquipID: equipmentId,
    oldTimeLineLegends
  })

  // Create/update Suppression Legends
  const suppressionData = inputData?.exceptions?.suppressed?.data || {}
  const suppressionLegends = generateSuppressionLegends({
    suppressionData,
    chartInfo,
    oldTimeLineLegends
  })

  // Assign Colors to new time line legends of all kinds
  assignAttributeStateToTLLegends(
    timeLineLegends,
    exceptionLegends,
    suppressionLegends,
    assignedAttributes,
    legendAttributesCombination,
    oldTimeLineLegends
  )

  const { timeLineBars, totalLanes } = transformTimeLinechartNew(
    timeLineLegends,
    newDataTimeline
  )

  const { exceptionsBars, exceptionLanes } = generateExceptionBars(
    exceptionData,
    exceptionLegends,
    chartInfo,
    totalLanes,
    startDate,
    endTimestamp,
    localTimeStamp
  )

  const suppressed =
    (inputData?.exceptions?.suppressed?.data &&
      inputData?.exceptions?.suppressed?.data[equipmentId]) ||
    {}
  const { suppressionBars } = generateSuppressionBars(
    suppressed,
    suppressionLegends,
    exceptionLanes,
    startDate,
    endTimestamp.format("YYYY-MM-DD")
  )

  const bars = [...timeLineBars, ...exceptionsBars, ...suppressionBars]

  const axisList = getMergedYAxisList(chartInfo, addedAxisList)

  legends = Object.values(legends)
  legends = legends?.filter((obj) => {
    if (circuitCount === 1) {
      return !(obj?.specificProperties?.propertyKey || "")?.includes(
        PROPERTY_CIRCUIT_CURRENT_DRAW_RLACALC
      )
    } else if (circuitCount > 1) {
      return (
        obj?.specificProperties?.propertyKey !==
        PROPERTY_CHILLER_LOAD_NORMALIZED
      )
    } else {
      return obj
    }
  })
  legends = legends?.map((obj) => {
    if (obj?.axisName?.toLowerCase() === "y") {
      if (circuitCount === 1) {
        return eq1CircuitCountXRef(obj, legends)
      } else if (circuitCount > 1) {
        return gt1CircuitCountXRef(obj, legends)
      } else {
        return obj
      }
    } else {
      return obj
    }
  })

  const noDataTimelineArray = Object.keys(timeLineLegends)?.map((key) => {
    const noData = (newDataTimeline?.[key]?.data || [])?.every(
      (e) => !Boolean(e) && e !== 0
    )
    return noData
  })

  const isNoDataTimeline = noDataTimelineArray?.every((e) => Boolean(e))

  const isNoTimeLineData = timeLineBars?.every((obj) => !Boolean(obj?.value))

  timeLineLegends = {
    ...timeLineLegends,
    ...exceptionLegends,
    ...suppressionLegends
  }

  return {
    chartData: {
      data: isNoData ? {} : newData,
      timelineData: isNoDataTimeline ? {} : newDataTimeline
    },
    chartProps: {
      legends: legends,
      timestamps: allTimestamps,
      axisList: axisList,
      timeLineLegends: timeLineLegends,
      timeLineBars:
        isNoData && isNoDataTimeline && isNoTimeLineData ? [] : bars,
      curveData: newCurveData || {},
      noDataAvailable: isNoData && isNoTimeLineData ? true : false,
      isdataprocessed: true,
      calculatedInterval: minimumCalculatedMin
    }
  }
}

export const eq1CircuitCountXRef = (obj, legends) => {
  const xRefLegend = legends?.find(
    (refObj) =>
      refObj?.specificProperties?.equipID ===
        obj?.specificProperties?.equipID &&
      refObj?.specificProperties?.propertyKey ===
        PROPERTY_CHILLER_LOAD_NORMALIZED
  )
  const newObj = {
    ...obj,
    xRef: xRefLegend?.id
  }
  return newObj
}

export const gt1CircuitCountXRef = (obj, legends) => {
  const ycktIndex = obj?.specificProperties?.propertyKey?.indexOf("CKT")
  const ycktNumberString = obj?.specificProperties?.propertyKey?.slice(
    ycktIndex,
    obj?.specificProperties?.propertyKey?.length
  )
  const yCurrentDrawProp =
    PROPERTY_CIRCUIT_CURRENT_DRAW_RLACALC + ycktNumberString
  const xRefLegend = legends?.find((refObj) => {
    return (
      refObj?.specificProperties?.equipID ===
        obj?.specificProperties?.equipID &&
      refObj?.specificProperties?.propertyKey === yCurrentDrawProp
    )
  })
  const newObj = {
    ...obj,
    xRef: xRefLegend?.id
  }
  return newObj
}

export const PROPERTY_CHILLER_LOAD_NORMALIZED = "ChillerLoadNormalized"
export const PROPERTY_CIRCUIT_CURRENT_DRAW_RLACALC = "CircuitCurrentDrawRLACalc"
